import { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'context/AuthContext';
import { canAction } from 'permissions';
import api from 'services/api';
import useEscapeClose from 'hooks/useEscapeClose';
import ClientPicker, { clientPayload, emptyClientValue } from 'components/shared/ClientPicker';
import downloadPdfBlob from 'components/shared/downloadPdfBlob';

const STATUT_COLORS = {
  brouillon: '#9e9e9e', envoye: '#1565c0', accepte: '#2e7d32',
  refuse: '#e53935', expire: '#f57c00', facture: '#7c3aed',
};

const EMPTY_FORM = {
  objet: '', date_validite: '', conditions: '', notes: '', remise_pourcent: 0,
};

const EMPTY_LIGNE = { produit_id: '', designation: '', description: '', quantite: 1, prix_unitaire_ht: 0, periodicite: 'ponctuel' };

export default function Devis() {
  const { user } = useAuth();
  const roles = user?.roles || [];
  const [devisList, setDevisList] = useState([]);
  const [clients, setClients] = useState([]);
  const [catalogue, setCatalogue] = useState([]);
  const [periodiciteDefauts, setPeriodiciteDefauts] = useState({}); // { categorie: periodicite } — pre-remplit la modale
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [clientValue, setClientValue] = useState(emptyClientValue);
  const [lignes, setLignes] = useState([{ ...EMPTY_LIGNE }]);
  const [detail, setDetail] = useState(null);
  const [search, setSearch] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [saving, setSaving] = useState(false);

  const canWrite = canAction(roles, 'devis', 'create');
  const canDelete = canAction(roles, 'devis', 'delete');

  useEscapeClose(showForm, () => setShowForm(false));

  const load = useCallback(async () => {
    try {
      const [d, c, cat, pdef] = await Promise.all([
        api.get('/devis'),
        api.get('/clients'),
        api.get('/catalogue'),
        api.get('/parametres/periodicite-defauts').catch(() => ({ data: {} })), // non bloquant : defaut vide si indispo
      ]);
      setDevisList(d.data);
      setClients(c.data);
      setCatalogue(cat.data);
      setPeriodiciteDefauts(pdef.data || {});
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = devisList.filter(d => {
    if (filterStatut && d.statut !== filterStatut) return false;
    if (search) {
      const s = search.toLowerCase();
      return d.numero?.toLowerCase().includes(s) || d.objet?.toLowerCase().includes(s) || d.client_nom?.toLowerCase().includes(s);
    }
    return true;
  });

  const totaux = filtered.reduce((acc, d) => {
    acc.total += Number(d.montant_ttc) || 0;
    if (d.statut === 'accepte') acc.accepte += Number(d.montant_ttc) || 0;
    return acc;
  }, { total: 0, accepte: 0 });

  const totalLignes = lignes.reduce((s, l) => s + (Number(l.quantite) || 1) * (Number(l.prix_unitaire_ht) || 0), 0);
  const remise = Number(form.remise_pourcent) || 0;
  const totalHT = totalLignes * (1 - remise / 100);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, ...clientPayload(clientValue), lignes };
      await api.post('/devis', payload);
      setShowForm(false);
      setForm(EMPTY_FORM);
      setClientValue(emptyClientValue());
      setLignes([{ ...EMPTY_LIGNE }]);
      load();
    } catch {} finally { setSaving(false); }
  };

  const updateStatut = async (id, statut) => {
    try {
      await api.put(`/devis/${id}`, { statut });
      load();
      if (detail?.id === id) setDetail({ ...detail, statut });
    } catch {}
  };

  const deleteDevis = async (id) => {
    if (!window.confirm('Supprimer ce devis ?')) return;
    try {
      await api.delete(`/devis/${id}`);
      load();
      if (detail?.id === id) setDetail(null);
    } catch {}
  };

  const openDetail = async (id) => {
    try {
      const res = await api.get(`/devis/${id}`);
      setDetail(res.data);
    } catch {}
  };

  const telechargerPDF = async (id, numero) => {
    try {
      const res = await api.get(`/devis/${id}/pdf`, { responseType: 'blob' });
      downloadPdfBlob(res.data, `devis-${numero || id}.pdf`);
    } catch (e) {
      console.error('Telechargement PDF devis echoue:', e);
      alert("Le PDF n'a pas pu etre telecharge. Reessayez.");
    }
  };

  const addLigne = () => setLignes([...lignes, { ...EMPTY_LIGNE }]);
  const removeLigne = (i) => setLignes(lignes.filter((_, idx) => idx !== i));
  const updateLigne = (i, field, value) => {
    const updated = [...lignes];
    updated[i] = { ...updated[i], [field]: value };
    setLignes(updated);
  };
  const selectProduit = (i, produitId) => {
    const p = catalogue.find(c => c.id === produitId);
    if (!p) return;
    const updated = [...lignes];
    // Pre-remplit la periodicite depuis le defaut tenant pour la CATEGORIE du produit.
    // Garde-fou : categorie non mappee / valeur hors liste -> on garde la valeur courante (defaut 'ponctuel'), jamais de recurrent impose.
    const VALIDES = ['ponctuel', 'mensuel', 'trimestriel', 'annuel'];
    const dflt = periodiciteDefauts[p.categorie];
    const periodicite = VALIDES.includes(dflt) ? dflt : (updated[i].periodicite || 'ponctuel');
    updated[i] = { ...updated[i], produit_id: p.id, designation: `${p.code} — ${p.designation}`, description: p.description || '', prix_unitaire_ht: Number(p.prix_unitaire_ht), periodicite };
    setLignes(updated);
  };

  if (detail) {
    return (
      <div>
        <button className="gss-btn" onClick={() => setDetail(null)} style={{ marginBottom: '1rem' }}>Retour</button>
        <div className="content-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <div>
              <h2 style={{ fontSize: 18, fontWeight: 600 }}>{detail.numero}</h2>
              <p style={{ color: 'var(--gss-text-secondary)', fontSize: 13 }}>{detail.objet}</p>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <span className="gss-badge" style={{ background: `${STATUT_COLORS[detail.statut]}15`, color: STATUT_COLORS[detail.statut] }}>{detail.statut}</span>
              <button className="gss-btn-sm" onClick={() => telechargerPDF(detail.id, detail.numero)}>Export PDF</button>
              {detail.statut === 'brouillon' && <button className="gss-btn-sm" onClick={() => updateStatut(detail.id, 'envoye')}>Envoyer</button>}
              {detail.statut === 'envoye' && <button className="gss-btn-sm" style={{ color: '#2e7d32' }} onClick={() => updateStatut(detail.id, 'accepte')}>Accepter</button>}
              {detail.statut === 'envoye' && <button className="gss-btn-sm gss-btn-danger" onClick={() => updateStatut(detail.id, 'refuse')}>Refuser</button>}
            </div>
          </div>

          <div className="gss-detail-grid">
            <div className="gss-detail-item"><label>Client</label><span>{detail.client_nom}</span></div>
            <div className="gss-detail-item"><label>Date</label><span>{detail.date_devis?.split('T')[0]}</span></div>
            <div className="gss-detail-item"><label>Validite</label><span>{detail.date_validite?.split('T')[0] || '-'}</span></div>
            <div className="gss-detail-item"><label>Montant TTC</label><span style={{ fontWeight: 700 }}>{Number(detail.montant_ttc).toLocaleString()} FCFA</span></div>
          </div>

          {detail.lignes?.length > 0 && (
            <div style={{ padding: '1rem 1.5rem' }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: '0.75rem' }}>Lignes du devis</h3>
              <table className="gss-table">
                <thead><tr><th>Designation</th><th>Qte</th><th>P.U.</th><th>Total</th></tr></thead>
                <tbody>
                  {detail.lignes.map((l, i) => (
                    <tr key={i}>
                      <td>{l.designation}</td>
                      <td>{Number(l.quantite)}</td>
                      <td style={{ textAlign: 'right' }}>{Number(l.prix_unitaire_ht).toLocaleString()}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>{Number(l.montant_ht).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {detail.conditions && (
            <div style={{ padding: '0 1.5rem 1rem' }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: '0.5rem' }}>Conditions</h3>
              <p style={{ color: 'var(--gss-text-secondary)', fontSize: 13, whiteSpace: 'pre-wrap' }}>{detail.conditions}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
<div className="stats-grid stats-grid--3">
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Total devis</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg></div></div>
          <div className="stat-card-value">{devisList.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Montant total</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg></div></div>
          <div className="stat-card-value">{totaux.total.toLocaleString()}</div>
          <div className="stat-card-sub">FCFA</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Acceptes</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg></div></div>
          <div className="stat-card-value" style={{ color: 'var(--gss-success)' }}>{totaux.accepte.toLocaleString()}</div>
          <div className="stat-card-sub">FCFA</div>
        </div>
      </div>

      <div className="content-card">
        <div className="gss-toolbar">
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <input className="gss-input" placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)} style={{ maxWidth: 250 }} />
            <select className="gss-input" value={filterStatut} onChange={e => setFilterStatut(e.target.value)} style={{ maxWidth: 160 }}>
              <option value="">Tous statuts</option>
              <option value="brouillon">Brouillon</option>
              <option value="envoye">Envoye</option>
              <option value="accepte">Accepte</option>
              <option value="refuse">Refuse</option>
              <option value="facture">Facture</option>
            </select>
          </div>
          {canWrite && <button className="gss-btn gss-btn-primary" onClick={() => setShowForm(true)}>+ Nouveau devis</button>}
        </div>

        {loading ? (
          <div className="skeleton-card"><div className="skeleton-row" /><div className="skeleton-row" /><div className="skeleton-row" /></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state"><div className="empty-state-title">Aucun devis</div></div>
        ) : (
          <div className="gss-table-wrap">
            <table className="gss-table">
              <thead><tr><th>Numero</th><th>Client</th><th>Objet</th><th>Montant TTC</th><th>Statut</th><th>Date</th><th>Actions</th></tr></thead>
              <tbody>
                {filtered.map(d => (
                  <tr key={d.id} style={{ cursor: 'pointer' }} onClick={() => openDetail(d.id)}>
                    <td style={{ fontWeight: 600 }}>{d.numero}</td>
                    <td>{d.client_nom}</td>
                    <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.objet}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{Number(d.montant_ttc).toLocaleString()}</td>
                    <td><span className="gss-badge" style={{ background: `${STATUT_COLORS[d.statut]}15`, color: STATUT_COLORS[d.statut] }}>{d.statut}</span></td>
                    <td style={{ color: 'var(--gss-text-secondary)' }}>{d.date_devis?.split('T')[0]}</td>
                    <td onClick={e => e.stopPropagation()}>
                      <div className="gss-actions">
                        <button className="gss-btn-icon" onClick={() => telechargerPDF(d.id, d.numero)} title="PDF">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14,2 14,8 20,8"/></svg>
                        </button>
                        {canDelete && d.statut === 'brouillon' && (
                          <button className="gss-btn-icon gss-btn-icon--delete" onClick={() => deleteDevis(d.id)} title="Supprimer">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><polyline points="3,6 5,6 21,6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <div className="gss-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="gss-modal gss-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                <h2>Nouveau devis</h2>
              </div>
              <div className="gss-modal-subtitle">Elaboration du devis</div>
              <button className="gss-modal-close" onClick={() => setShowForm(false)}>x</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div style={{ padding: '1.5rem' }}>
                <div className="gss-modal-section">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/>
                  </svg>
                  <span>INFORMATIONS GENERALES</span>
                </div>
                <div className="gss-form-grid">
                  <div className="gss-field gss-field-full">
                    <ClientPicker clients={clients} value={clientValue} onChange={setClientValue} roles={roles} />
                  </div>
                  <div className="gss-field">
                    <label>Date validite</label>
                    <input className="gss-input" type="date" value={form.date_validite} onChange={e => setForm({ ...form, date_validite: e.target.value })} />
                  </div>
                  <div className="gss-field gss-field-full">
                    <label>Objet *</label>
                    <input className="gss-input" value={form.objet} onChange={e => setForm({ ...form, objet: e.target.value })} required />
                  </div>
                </div>

                <div className="gss-modal-section">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
                  </svg>
                  <span>LIGNES DU DEVIS</span>
                </div>
                {lignes.map((l, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 60px 100px 120px 80px 40px', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
                    <select className="gss-input" value={l.produit_id} onChange={e => selectProduit(i, e.target.value)} required>
                      <option value="">-- Produit catalogue --</option>
                      {catalogue.filter(c => c.actif !== false).map(c => (
                        <option key={c.id} value={c.id}>{c.code} — {c.designation} ({Number(c.prix_unitaire_ht).toLocaleString()} F)</option>
                      ))}
                    </select>
                    <input className="gss-input" type="number" min="1" placeholder="Qte" value={l.quantite} onChange={e => updateLigne(i, 'quantite', e.target.value)} />
                    <input className="gss-input" type="number" min="0" value={l.prix_unitaire_ht} readOnly style={{ background: '#f5f5f5' }} />
                    <select className="gss-input" value={l.periodicite || 'ponctuel'} onChange={e => updateLigne(i, 'periodicite', e.target.value)} title="Periodicite de la ligne">
                      <option value="ponctuel">Ponctuel</option>
                      <option value="mensuel">Mensuel</option>
                      <option value="trimestriel">Trimestriel</option>
                      <option value="annuel">Annuel</option>
                    </select>
                    <span style={{ fontSize: 12, fontWeight: 600, textAlign: 'right' }}>{((Number(l.quantite) || 1) * (Number(l.prix_unitaire_ht) || 0)).toLocaleString()} F</span>
                    {lignes.length > 1 && (
                      <button type="button" className="gss-btn-icon gss-btn-icon--delete" onClick={() => removeLigne(i)}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" className="gss-btn-sm" onClick={addLigne}>+ Ligne</button>

                <div style={{ marginTop: '1rem', padding: 12, background: '#F8F8F8', borderRadius: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', alignItems: 'center' }}>
                    <span style={{ fontSize: 13, color: 'var(--gss-text-secondary)' }}>Remise %</span>
                    <input className="gss-input" type="number" min="0" max="100" style={{ width: 80 }} value={form.remise_pourcent} onChange={e => setForm({ ...form, remise_pourcent: e.target.value })} />
                    <span style={{ fontSize: 16, fontWeight: 700 }}>Total : {totalHT.toLocaleString()} FCFA</span>
                  </div>
                  <div style={{ fontSize: 11, color: '#888', fontStyle: 'italic', textAlign: 'right', marginTop: 4 }}>Exonere de TVA — Regime simplifie</div>
                </div>

                <div className="gss-modal-section">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
                  </svg>
                  <span>CONDITIONS & NOTES</span>
                </div>
                <div className="gss-form-grid">
                  <div className="gss-field gss-field-full">
                    <label>Conditions</label>
                    <textarea className="gss-input" rows="3" style={{ height: 'auto', padding: '8px 12px' }} value={form.conditions} onChange={e => setForm({ ...form, conditions: e.target.value })} />
                  </div>
                  <div className="gss-field gss-field-full">
                    <label>Notes internes</label>
                    <textarea className="gss-input" rows="2" style={{ height: 'auto', padding: '8px 12px' }} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
                  </div>
                </div>
              </div>
              <div className="gss-modal-actions">
                <button type="button" className="gss-btn-cancel" onClick={() => setShowForm(false)}>Annuler</button>
                <button type="submit" className="gss-btn-deploy" disabled={saving}>{saving ? 'Enregistrement...' : <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 2L11 13"/><path d="M22 2L15 22l-4-9-9-4z"/></svg>Creer le devis</>}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
